import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { setNavigate } from "@/lib/navigation-service";
import { GrowthBookAttributesSync } from "@/lib/growthbook-attributes";
import { trackLastVisitedRoute } from "@/features/auth-gate";
import { pushDataLayer } from "@/lib/analytics";
import { ConsentBanner } from "@/components/common/ConsentBanner";
import { ToastContainer } from "@/features/toast";
import { BreadcrumbProvider } from "@/features/breadcrumb";
import { Modal } from "@/components/ui/modal";
import { PaymentRequiredModal } from "@/components/ui/payment-required-modal";
import { VoiceChat, MiniVoiceChat } from "@/features/voice-chat";
import { SidebarProvider } from "@/components/ui/sidebar";
import { LLMSelectionDialog } from "@/features/llm-selection";
import { AuthGateDialog } from "@/features/auth-gate";
import { ScreenshotCaptureOverlay } from "@/features/support/components/ScreenshotCaptureOverlay";
import { EnhancedAIModelSelectorDialog } from "@/features/ai-model-selection";
import {
  PaymentModal,
  PaymentNavigationHandler,
  PriceIdCheckoutHandler,
} from "@/features/payment";
import { ProductDetailModal } from "@/pages/ProductDetailModal";
import { QuickSettings } from "@/components/common/QuickSettings";
import { FilePickerModalWrapper } from "@/features/file-picker/components/FilePickerModalWrapper";
import { GlobalSearchDialog } from "@/features/search";
import { ChatDropOverlay } from "@/features/chat/components";
import { AgreementModal } from "@/features/agreements";
import { ShareLinkDialog } from "@/features/chat/share/components/ShareLinkDialog";
import { CommandSpotlight } from "@/features/slash-commands";
import { ModuleThemeSync, useActiveModule } from "@/modules";
import { ModuleTransitionVeil } from "@/features/module-hub";

function NavigationInitializer() {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
}

/**
 * Rota değişimlerinde iki iş yapar:
 *
 * 1. Auth dışı son ziyaret edilen sayfayı izler; login/register sonrası
 *    kullanıcı kaldığı yerden devam edebilsin diye fallback dönüş adresi sağlar.
 * 2. dataLayer'a `virtual_page_view` yazar. SPA olduğumuz için ilk yükleme
 *    dışında GTM'e hiçbir sayfa görüntüleme ulaşmıyordu; `chat/` ve `wizard/`
 *    altındaki her şey bu yüzden ölçüm dışıydı. `module` alanı hangi modülde
 *    olduğumuzu raporlar (shared sayfalarda boş).
 */
function RouteChangeTracker() {
  const location = useLocation();
  const activeModule = useActiveModule();
  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    trackLastVisitedRoute(path);
    pushDataLayer({
      event: "virtual_page_view",
      page_path: path,
      page_title: document.title,
      module: activeModule?.id ?? "",
    });
  }, [location.pathname, location.search, activeModule?.id]);
  return null;
}

/**
 * Root layout route. Router context'i isteyen her şey burada: provider'lar,
 * global modal'lar, izleyiciler. Sayfalar `<Outlet />` üzerinden gelir.
 * Uygulama-geneli init effect'leri (credits, cart, LLM auto-select…) App.tsx'te.
 */
export function AppRoot() {
  return (
    <BreadcrumbProvider>
      <SidebarProvider>
        <script>var netmera = netmera || []</script>
        <script
          src={`https://cdn.netmera-web.com/wsdkjs/${import.meta.env.VITE_NETMERA_KEY}`}
          async
        ></script>
        <CommandSpotlight />

        {/* Aktif modülün hue'sunu ve data-module'ünü <html>'e yazar (Faz 1 tema) */}
        <ModuleThemeSync />

        <Outlet />

        {/* Hub kartı → modül geçişinin tam ekran perdesi; route değişiminde kalır */}
        <ModuleTransitionVeil />

        {/* Navigation Service Initializer - Router context içinde olmalı */}
        <NavigationInitializer />

        {/* Login sonrası "kaldığın yerden devam" için rota takibi */}
        <RouteChangeTracker />

        {/* GrowthBook targeting attribute'ları (ülke, dil, cihaz) */}
        <GrowthBookAttributesSync />

        {/* Consent Mode v2 — cevap verilene kadar reklam/analitik
            depolaması "denied". Varsayılanlar index.html'de. */}
        <ConsentBanner />

        {/* Checkout Navigation Handler - Router içinde olmalı */}
        <PaymentNavigationHandler />
        <PriceIdCheckoutHandler />

        {/* Quick Settings - Always visible */}
        <QuickSettings />

        {/* Global Components */}
        <Modal />
        {/* Payment Required Modal - Merkezi 402 handler */}
        <PaymentRequiredModal />
        <ToastContainer />

        {/* Voice Chat Components - Zero Props */}
        <VoiceChat />
        <MiniVoiceChat />

        {/* LLM Selection Dialog - Zero Props */}
        <LLMSelectionDialog />

        {/* Share Link Dialog - Zero Props */}
        <ShareLinkDialog />

        {/* Enhanced AI Model Selector Dialog - Zero Props */}
        <EnhancedAIModelSelectorDialog />

        {/* Checkout Modal - Zero Props */}
        <PaymentModal />

        {/* Global Search Dialog - Zero Props */}
        <GlobalSearchDialog />

        {/* Product Detail Modal - Router context içinde */}
        <ProductDetailModal />

        {/* File Picker Modal - Zero Props */}
        <FilePickerModalWrapper />

        {/* Tam sayfa sohbet ve sohbeti açık modüllerde global dosya bırakma */}
        <ChatDropOverlay />

        {/* Agreement Modal - Zero Props */}
        <AgreementModal />

        {/* Auth Gate Dialog - Zero Props (public modda auth gerektiren aksiyonlar için) */}
        <AuthGateDialog />

        {/* Destek ekran görüntüsü yakalama modu - Zero Props */}
        <ScreenshotCaptureOverlay />
      </SidebarProvider>
    </BreadcrumbProvider>
  );
}
